/**
 * The dashboard Overview body and header readouts (spec 2026-10-09 §2.2 tab 1, §7.1; W4 card C1). Renders the
 * `OverviewData` snapshot the shell polls at its refresh rate while open: six cards whose titles open their tab or
 * section, the alerts' Jump to / Send wing (the fleet alert hooks), the unread chips from the section badges, the
 * Obligations Pay `DockedAction` and the Active job's Set course (C0's resolvers, wired in W4 integration).
 */
import type { ReactElement } from 'react'
import { runAlertAction, type FleetAlertAction } from '../../../../fleet-link/presence'
import type { FleetAlert } from '../../../../strategic/types'
import { loadDashboardConfig } from '../../config'
import { DockedAction } from '../../parts'
import { closeDashboard, type DashCommandPort } from '../../registry'
import type { CourseTarget } from '../jobs/model'
import { EMPTY_OVERVIEW_INPUT, loadOverviewConfig, overviewModel, unreadChips, type OvCard, type OvHeader, type OvLine, type OverviewData } from './model'
import './overview.css'

function toneClass(base: string, tone: OvLine['tone']): string {
  return tone === undefined ? base : `${base} ov-tone--${tone}`
}

function AlertActions({ line, alerts }: { line: OvLine; alerts: readonly FleetAlert[] }): ReactElement | null {
  const a = line.alert
  if (a === undefined || (!a.jump && !a.wing)) return null
  const cfg = loadOverviewConfig().alerts
  const run = (action: FleetAlertAction): void => {
    const alert = alerts[a.index]
    // Jump to opens the galaxy map: leave the dashboard for it (as Fleet › Feed does).
    if (alert !== undefined && runAlertAction(alert, action) && action === 'jumpTo') closeDashboard(false)
  }
  return (
    <span className="ov-actions">
      {a.jump ? (
        <button type="button" className="ov-action" data-ov-action="jumpTo" onClick={() => run('jumpTo')}>
          {cfg.jumpTo}
        </button>
      ) : null}
      {a.wing ? (
        <button type="button" className="ov-action" data-ov-action="sendWing" onClick={() => run('sendWing')}>
          {cfg.sendWing}
        </button>
      ) : null}
    </span>
  )
}

type CourseFn = ((target: CourseTarget) => void) | undefined

function Card({
  card,
  port,
  alerts,
  badges,
  course,
}: {
  card: OvCard
  port: DashCommandPort
  alerts: readonly FleetAlert[]
  badges: Readonly<Record<string, number>>
  course: CourseFn
}): ReactElement {
  const chips = card.id === 'alerts' ? unreadChips(badges, loadOverviewConfig()) : []
  const text = loadDashboardConfig().dockedAction
  const docked = card.docked
  const target = card.course
  return (
    <section className="dash-card ui-panel ov-card" data-dash-card={card.id}>
      <h3 className="dash-card__title">
        <button type="button" className="ov-link" data-ov-link={card.id} onClick={() => port.open(card.link)}>
          {card.title}
          <span className="ov-link__to">{card.linkLabel} ▸</span>
        </button>
      </h3>
      {card.bars.length > 0 ? (
        <div className="ov-bars">
          {card.bars.map((b) => (
            <div key={b.id} className={`ov-bar ov-bar--${b.id}`} data-ov-bar={b.id} data-ov-pct={b.pct}>
              <span className="ov-bar__label">{b.label}</span>
              <span className="ov-bar__track">
                <span className="ov-bar__fill" style={{ width: `${b.pct}%` }} />
              </span>
              <span className="ov-bar__value">{b.pct}</span>
            </div>
          ))}
        </div>
      ) : null}
      {card.lines.length > 0 ? (
        <dl className="ov-lines">
          {card.lines.map((l, i) => (
            <div key={i} className={toneClass('ov-line', l.tone)}>
              {l.label !== undefined ? <dt className="ov-line__label">{l.label}</dt> : null}
              <dd className="ov-line__text">
                {l.text}
                <AlertActions line={l} alerts={alerts} />
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {card.empty !== null ? <p className="ov-empty">{card.empty}</p> : null}
      {docked !== undefined ? (
        <DockedAction
          cfg={text}
          label={docked.label}
          reason={docked.reason}
          nearest={docked.nearest}
          onSetCourse={() => {
            if (docked.stationId !== null) course?.({ kind: 'station', id: docked.stationId })
          }}
        />
      ) : null}
      {target !== undefined && course !== undefined ? (
        <p className="ov-course">
          <button type="button" className="dash-docked__course" data-ov-course={card.id} onClick={() => course(target)}>
            <kbd>F</kbd> {text.setCourse}
          </button>
        </p>
      ) : null}
      {chips.length > 0 ? (
        <p className="ov-chips">
          {chips.map((c) => (
            <button key={c.section} type="button" className="ov-chip" data-ov-unread={c.section} onClick={() => port.open({ section: c.section })}>
              {c.label} <span className="dash-count">{c.count}</span>
            </button>
          ))}
        </p>
      ) : null}
    </section>
  )
}

export function OverviewBody({ data, port, badges }: { data: OverviewData | null; port: DashCommandPort; badges: Readonly<Record<string, number>> }): ReactElement {
  // Before the first poll (or without a source) every card shows its empty line.
  const view = data?.view ?? overviewModel(EMPTY_OVERVIEW_INPUT)
  const alerts = data?.alerts ?? []
  return (
    <div className="dash-overview" data-dash-body="overview">
      {view.cards.map((c) => (
        <Card key={c.id} card={c} port={port} alerts={alerts} badges={badges} course={data?.course} />
      ))}
    </div>
  )
}

/** The header's location, credits and hull (spec §7.1); fields without data are left out. */
export function OverviewHeader({ header }: { header: OvHeader | null }): ReactElement | null {
  if (header === null) return null
  return (
    <span className="ov-header" data-ov-header="">
      {header.location !== null ? (
        <span className="ov-header__item" data-ov-header-location="">
          {header.location}
        </span>
      ) : null}
      {header.credits !== null ? (
        <span className="ov-header__item" data-ov-header-credits="">
          {header.credits}
        </span>
      ) : null}
      {header.hull !== null ? (
        <span className={toneClass('ov-header__item', header.hullTone)} data-ov-header-hull="">
          {header.hull}
        </span>
      ) : null}
    </span>
  )
}
